import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  X, 
  Save, 
  Loader, 
  Star,
  User,
  MapPin,
  MessageSquare,
  Package,
  Settings,
  Eye,
  Sparkles,
  Award,
  Quote,
  Edit
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AdminTestimonial } from '../../services/contentService';

interface EditTestimonialModalProps {
  isOpen: boolean;
  onClose: () => void;
  testimonial: AdminTestimonial | null;
  onSubmit: (testimonialId: string, testimonialData: Partial<AdminTestimonial>) => Promise<void>;
}

const statusOptions = [
  { value: 'published', label: 'Published', color: 'bg-green-100 text-green-800', emoji: '✅', description: 'Visible on customer-facing app' },
  { value: 'unpublished', label: 'Unpublished', color: 'bg-gray-100 text-gray-800', emoji: '📝', description: 'Hidden from customer-facing app' }
];

const backgroundColors = [
  { value: 'from-blue-500 to-indigo-600', label: 'Blue Gradient', preview: 'from-blue-500 to-indigo-600' },
  { value: 'from-purple-500 to-pink-600', label: 'Purple Gradient', preview: 'from-purple-500 to-pink-600' },
  { value: 'from-teal-500 to-cyan-600', label: 'Teal Gradient', preview: 'from-teal-500 to-cyan-600' },
  { value: 'from-emerald-500 to-green-600', label: 'Green Gradient', preview: 'from-emerald-500 to-green-600' },
  { value: 'from-orange-500 to-red-600', label: 'Orange Gradient', preview: 'from-orange-500 to-red-600' },
  { value: 'from-indigo-500 to-purple-600', label: 'Indigo Gradient', preview: 'from-indigo-500 to-purple-600' }
];

export function EditTestimonialModal({ isOpen, onClose, testimonial, onSubmit }: EditTestimonialModalProps) {
  const [formData, setFormData] = useState({
    customerName: '',
    customerLocation: '',
    rating: 5,
    content: '',
    productId: '',
    status: 'published' as 'published' | 'unpublished',
    isVerified: true,
    avatar: '',
    bgColor: backgroundColors[0].value
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form data when testimonial changes
  useEffect(() => {
    if (testimonial) {
      setFormData({
        customerName: testimonial.customerName || '',
        customerLocation: testimonial.customerLocation || '',
        rating: testimonial.rating || 5,
        content: testimonial.content || '',
        productId: testimonial.productId || '',
        status: testimonial.status || 'published',
        isVerified: testimonial.isVerified ?? true,
        avatar: testimonial.avatar || '',
        bgColor: testimonial.bgColor || backgroundColors[0].value
      });
    }
  }, [testimonial]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Testimonial content is required';
    }

    if (formData.rating < 1 || formData.rating > 5) {
      newErrors.rating = 'Rating must be between 1 and 5';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Create testimonial data with all fields included
      const testimonialData: Partial<AdminTestimonial> = {
        customerName: formData.customerName.trim(),
        customerLocation: formData.customerLocation.trim() || '', // Always include, empty string if cleared
        rating: formData.rating,
        content: formData.content.trim(),
        productId: formData.productId.trim() || '', // Always include, empty string if cleared
        status: formData.status,
        isVerified: formData.isVerified,
        avatar: formData.avatar.trim() || '', // Always include, empty string if cleared
        bgColor: formData.bgColor
      };

      console.log('Admin Portal - Updating testimonial data:', testimonialData);
      console.log('Admin Portal - bgColor value:', formData.bgColor, 'type:', typeof formData.bgColor);

      await onSubmit(testimonial!.id, testimonialData);
      toast.success('Testimonial updated successfully');
      handleClose();
    } catch (error) {
      console.error('Error updating testimonial:', error);
      toast.error('Failed to update testimonial');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      customerName: '',
      customerLocation: '',
      rating: 5,
      content: '',
      productId: '',
      status: 'published',
      isVerified: true,
      avatar: '',
      bgColor: backgroundColors[0].value
    });
    setErrors({});
    onClose();
  };

  const handleRatingChange = (rating: number) => {
    setFormData({ ...formData, rating });
  };

  const getCustomerInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!isOpen || !testimonial) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-jamaica-green to-emerald-500 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <Edit className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    Edit Testimonial
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm"
                  >
                    Update customer feedback and review details
                  </motion.p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Form */}
          <div className="p-8 space-y-8 overflow-y-auto flex-1 bg-gradient-to-b from-gray-50/50 to-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Form Fields */}
              <div className="space-y-6">
                {/* Customer Information */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-4"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <User className="h-4 w-4 text-jamaica-green" />
                    Customer Information
                  </Label>
                  
                  <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm space-y-4">
                    {/* Customer Name */}
                    <div className="space-y-2">
                      <Label htmlFor="customerName" className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <User className="h-3 w-3" />
                        Full Name *
                      </Label>
                      <Input
                        id="customerName"
                        placeholder="John Smith"
                        value={formData.customerName}
                        onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                        className={`transition-all duration-200 ${
                          errors.customerName 
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                            : 'border-gray-200 focus:border-jamaica-green focus:ring-jamaica-green/20'
                        } rounded-xl h-12 shadow-sm hover:shadow-md focus:shadow-lg`}
                      />
                      {errors.customerName && (
                        <motion.p 
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="text-sm text-red-600 flex items-center gap-1"
                        >
                          <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                          {errors.customerName}
                        </motion.p>
                      )}
                    </div>

                    {/* Customer Location */}
                    <div className="space-y-2">
                      <Label htmlFor="customerLocation" className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <MapPin className="h-3 w-3" />
                        Location (Optional)
                      </Label>
                      <Input
                        id="customerLocation"
                        placeholder="New York, USA"
                        value={formData.customerLocation}
                        onChange={(e) => setFormData({ ...formData, customerLocation: e.target.value })}
                        className={`transition-all duration-200 ${
                          errors.customerLocation 
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                            : 'border-gray-200 focus:border-jamaica-green focus:ring-jamaica-green/20'
                        } rounded-xl h-12 shadow-sm hover:shadow-md focus:shadow-lg`}
                      />
                      {errors.customerLocation && (
                        <motion.p 
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="text-sm text-red-600 flex items-center gap-1"
                        >
                          <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                          {errors.customerLocation}
                        </motion.p>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Rating */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-3"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Award className="h-4 w-4 text-jamaica-green" />
                    Customer Rating
                  </Label>
                  <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <motion.button
                            key={star}
                            type="button"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleRatingChange(star)}
                            className="focus:outline-none"
                          >
                            <Star
                              className={`w-8 h-8 transition-colors duration-200 ${
                                star <= formData.rating
                                  ? 'text-jamaica-yellow fill-jamaica-yellow'
                                  : 'text-gray-300 hover:text-jamaica-yellow/50'
                              }`}
                            />
                          </motion.button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-gray-900">{formData.rating}</span>
                        <span className="text-gray-500">out of 5 stars</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Testimonial Content */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-3"
                >
                  <Label htmlFor="content" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Quote className="h-4 w-4 text-jamaica-green" />
                    Testimonial Content *
                  </Label>
                  <Textarea
                    id="content"
                    placeholder="Share the customer's experience and feedback in their own words..."
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className={`transition-all duration-200 resize-none ${
                      errors.content 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-200 focus:border-jamaica-green focus:ring-jamaica-green/20'
                    } rounded-xl text-base p-4 min-h-[120px] shadow-sm hover:shadow-md focus:shadow-lg`}
                    rows={4}
                  />
                  {errors.content && (
                    <motion.p 
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="text-sm text-red-600 flex items-center gap-1"
                    >
                      <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                      {errors.content}
                    </motion.p>
                  )}
                </motion.div>

                {/* Product Information */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="space-y-4"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Package className="h-4 w-4 text-jamaica-green" />
                    Product Information (Optional)
                  </Label>
                  
                  <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                    <div className="space-y-2">
                      <Label htmlFor="productId" className="text-sm font-medium text-gray-900">
                        Product ID
                      </Label>
                      <Input
                        id="productId"
                        value={formData.productId}
                        onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                        className="h-12 rounded-xl border-gray-200 focus:border-jamaica-green focus:ring-jamaica-green/20"
                      />
                      <p className="text-xs text-gray-500">Enter the product ID to link this testimonial to a specific product</p>
                    </div>
                  </div>
                </motion.div>

                {/* Visual Customization */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="space-y-4"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-jamaica-green" />
                    Avatar Background
                  </Label>
                  
                  <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm space-y-4">
                    <div className="space-y-3">
                      <Label className="text-sm font-medium text-gray-900">Background Gradient</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {backgroundColors.map((bg, index) => (
                          <motion.div
                            key={index}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setFormData({ ...formData, bgColor: bg.value })}
                            className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                              formData.bgColor === bg.value
                                ? 'border-jamaica-green shadow-lg shadow-jamaica-green/20'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className={`h-8 w-full rounded-lg bg-gradient-to-br ${bg.preview} mb-2`}></div>
                            <span className="text-xs font-medium text-gray-900">{bg.label}</span>
                            {formData.bgColor === bg.value && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute top-1 right-1 w-5 h-5 bg-jamaica-green rounded-full flex items-center justify-center"
                              >
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </motion.div>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Publication Status */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="space-y-4"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Settings className="h-4 w-4 text-jamaica-green" />
                    Publication Status
                  </Label>
                  
                  <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm space-y-4">
                    <div className="grid grid-cols-1 gap-3">
                      {statusOptions.map((status) => (
                        <motion.div
                          key={status.value}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setFormData({ ...formData, status: status.value as any })}
                          className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                            formData.status === status.value
                              ? 'border-jamaica-green bg-jamaica-green/5'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{status.emoji}</span>
                            <div>
                              <span className="font-medium text-gray-900">{status.label}</span>
                              <p className="text-sm text-gray-500">{status.description}</p>
                            </div>
                          </div>
                          {formData.status === status.value && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="absolute top-2 right-2 w-5 h-5 bg-jamaica-green rounded-full flex items-center justify-center"
                            >
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            </motion.div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Right Column - Live Preview */}
              <div className="space-y-4">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  className="sticky top-0"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-4">
                    <Eye className="h-4 w-4 text-jamaica-green" />
                    Live Preview
                  </Label>
                  
                  <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 rounded-xl p-6 border border-gray-200">
                    {formData.customerName && formData.content ? (
                      <div className="group/testimonial relative">
                        <div className="relative bg-white/90 backdrop-blur-md rounded-2xl p-6 shadow-lg border-2 border-white/50 transform overflow-hidden">
                          
                          {/* Quote Icon */}
                          <div className="absolute top-4 right-4 opacity-20">
                            <Quote className="w-8 h-8 text-jamaica-green transform rotate-12" />
                          </div>

                          {/* Rating Stars */}
                          <div className="flex mb-4 relative z-10">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < formData.rating
                                    ? "text-jamaica-yellow fill-jamaica-yellow"
                                    : "text-gray-300"
                                }`}
                              />
                            ))}
                            <div className="ml-2 bg-jamaica-green/10 px-2 py-1 rounded-full">
                              <span className="text-xs font-semibold text-jamaica-green">✓ Verified</span>
                            </div>
                          </div>
                          
                          {/* Testimonial Content */}
                          <blockquote className="text-gray-700 mb-4 leading-relaxed italic relative z-10 text-sm">
                            "{formData.content}"
                          </blockquote>
                          
                          {/* Author Info */}
                          <div className="flex items-center gap-3 relative z-10">
                            {/* Avatar */}
                            <div className={`w-10 h-10 bg-gradient-to-br ${formData.bgColor} rounded-full flex items-center justify-center shadow-lg`}>
                              <span className="text-white font-bold text-base">
                                {getCustomerInitials(formData.customerName)}
                              </span>
                            </div>
                            
                            {/* Author Details */}
                            <div className="flex-1">
                              <p className="font-bold text-gray-900 text-sm">
                                {formData.customerName}
                              </p>
                              {formData.customerLocation && formData.customerLocation.trim() !== '' && (
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <MapPin className="w-3 h-3" />
                                  <span>{formData.customerLocation}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
                        <Quote className="h-12 w-12 text-gray-300 mb-4" />
                        <p className="text-sm">Fill in the form to see a preview</p>
                        <p className="text-xs text-gray-400 mt-1">This is how your testimonial will appear on the customer-facing app</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Enhanced Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-end gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  variant="outline" 
                  onClick={handleClose} 
                  disabled={isSubmitting}
                  className="px-6 h-11 rounded-xl font-medium"
                >
                  Cancel
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting || !formData.customerName.trim() || !formData.content.trim()}
                  className="bg-gradient-to-r from-jamaica-green to-emerald-500 hover:from-jamaica-green/90 hover:to-emerald-500/90 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-jamaica-green/25"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Updating Testimonial...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Update Testimonial
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
} 